import { Link } from "react-router-dom";
import {
  ArrowUpRight, Check, Building2, UserRound, Store, Shuffle, MessagesSquare,
  CalendarDays, Handshake, BookOpen, BadgeCheck, Sparkles, Send, Target, TrendingUp,
} from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import StatusBadge from "../components/StatusBadge";
import { GroupIllustration } from "../components/Illustrations";
import { FEATURE_GROUPS, FEATURES } from "../data/content";

const ICONS = { Building2, UserRound, Store, Shuffle, MessagesSquare, CalendarDays, Handshake, BookOpen, BadgeCheck, Sparkles, Send, Target, TrendingUp };
const byKey = Object.fromEntries(FEATURES.map((f) => [f.key, f]));

function FeatureRow({ feature }) {
  const Icon = ICONS[feature.icon];
  return (
    <div className="hub-card rounded-2xl p-6">
      <div className="flex items-center justify-between">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[color:var(--hairline)] bg-[color:var(--soft)] text-[color:var(--ink)]">{Icon && <Icon className="h-5 w-5" />}</span>
        <StatusBadge status={feature.status} />
      </div>
      <h3 className="font-display mt-4 text-lg font-semibold tracking-tight text-[color:var(--ink)]">{feature.name}</h3>
      <p className="mt-2 text-sm leading-relaxed text-[color:var(--ink-soft)]">{feature.purpose}</p>
      <ul className="mt-4 space-y-2">
        {feature.benefits.slice(0, 3).map((b) => (
          <li key={b} className="flex items-start gap-2 text-sm text-[color:var(--ink-soft)]"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-[color:var(--brand-teal)]" />{b}</li>
        ))}
      </ul>
      <p className="mt-4 border-t border-[color:var(--hairline)] pt-3 text-xs text-[color:var(--ink-soft)]"><span className="font-semibold text-[color:var(--ink)]">Next: </span>{feature.future}</p>
    </div>
  );
}

export default function Features() {
  return (
    <>
      <Seo title="Features" description="Explore the full StUpEco Hub feature set grouped into Connect, Discover, Collaborate and Grow — directories, marketplace, community, events, cross-selling, verification and more." path="/features" />
      <section className="relative pt-32 pb-14 lg:pt-40" data-testid="features-hero">
        <div className="grid-lines absolute inset-0 -z-10 opacity-50" />
        <div className="container-hub">
          <Reveal><span className="eyebrow"><span className="brand-bar h-1.5 w-1.5 rounded-full" /> Features</span></Reveal>
          <Reveal delay={0.05}><h1 className="font-display mt-5 max-w-4xl text-4xl font-semibold leading-[1.02] tracking-[-0.03em] text-[color:var(--ink)] sm:text-5xl lg:text-6xl">Everything a business needs to discover, connect and grow.</h1></Reveal>
          <Reveal delay={0.1}><p className="mt-6 max-w-2xl text-base leading-relaxed text-[color:var(--ink-soft)] sm:text-lg">The Hub's capabilities are organised around four stages of growth — Connect, Discover, Collaborate and Grow.</p></Reveal>
          <Stagger className="mt-8 flex flex-wrap gap-2.5" gap={0.05}>
            {FEATURE_GROUPS.map((g) => (
              <StaggerItem key={g.id}>
                <a href={`#${g.id}`} className="inline-flex items-center gap-2 rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] px-4 py-2 text-sm font-semibold text-[color:var(--ink)] transition-colors hover:border-[color:var(--ink)]">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: g.accent }} />{g.title}
                </a>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {FEATURE_GROUPS.map((group, gi) => {
        const reversed = gi % 2 === 1;
        return (
          <section key={group.id} id={group.id} className={`py-16 lg:py-24 ${gi % 2 === 1 ? "bg-soft" : ""}`} data-testid={`feature-group-${group.id}`}>
            <div className="container-hub">
              <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-14">
                <div className={`lg:col-span-5 lg:sticky lg:top-28 ${reversed ? "lg:order-2" : ""}`}>
                  <Reveal>
                    <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: group.accent }}>
                      <span className="font-display text-2xl">{String(gi + 1).padStart(2, "0")}</span>{group.title}
                    </span>
                    <h2 className="font-display mt-4 text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">{group.title}</h2>
                    <p className="mt-4 max-w-md text-base leading-relaxed text-[color:var(--ink-soft)]">{group.intro}</p>
                    <div className="mt-8 hub-card overflow-hidden rounded-2xl bg-[color:var(--soft)] p-6"><GroupIllustration variant={group.variant} className="w-full" /></div>
                  </Reveal>
                </div>
                <div className={`lg:col-span-7 ${reversed ? "lg:order-1" : ""}`}>
                  <Stagger className="grid grid-cols-1 gap-5 sm:grid-cols-2" gap={0.07}>
                    {group.keys.map((k) => byKey[k] && (
                      <StaggerItem key={k}><FeatureRow feature={byKey[k]} /></StaggerItem>
                    ))}
                  </Stagger>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <section className="py-16 lg:py-24">
        <div className="container-hub">
          <div className="flex flex-col items-start justify-between gap-6 rounded-3xl bg-[color:var(--invert-bg)] p-10 text-white sm:flex-row sm:items-center lg:p-14">
            <div>
              <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Be first in line as features go live.</h2>
              <p className="mt-2 text-white/70">Join the early-access list today.</p>
            </div>
            <Link to="/join" data-testid="features-cta-join" className="group inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-[color:var(--ink)] transition-transform duration-200 hover:-translate-y-0.5">Join Hub <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
